import React from 'react';

/**
 * Aria — IHG IND's female AI assistant avatar.
 *
 * A brand-aligned SVG portrait: warm skin tone, dark hair, subtle bronze glow
 * halo (echoes the site's bronze accent), and a small "AI" spark to hint at
 * intelligence. Sized via className / props so the same component renders
 * cleanly at 32px (chat header) up to 128px (welcome card).
 */
export const AIAssistantAvatar = ({ size = 40, className = '', showHalo = true }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 64 64"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
    role="img"
    aria-label="Aria AI assistant avatar"
    data-testid="ai-assistant-avatar"
  >
    <defs>
      <linearGradient id="aria-halo" x1="0" y1="0" x2="64" y2="64">
        <stop offset="0%" stopColor="#C89B5D" stopOpacity="0.55" />
        <stop offset="100%" stopColor="#C89B5D" stopOpacity="0" />
      </linearGradient>
      <linearGradient id="aria-hair" x1="0" y1="0" x2="0" y2="64">
        <stop offset="0%" stopColor="#1F1B18" />
        <stop offset="100%" stopColor="#3B322A" />
      </linearGradient>
      <linearGradient id="aria-skin" x1="0" y1="0" x2="0" y2="64">
        <stop offset="0%" stopColor="#F5D9B8" />
        <stop offset="100%" stopColor="#E7BD8C" />
      </linearGradient>
    </defs>

    {/* Bronze halo — signals "AI / powered by intelligence" */}
    {showHalo && (
      <circle cx="32" cy="32" r="31" fill="url(#aria-halo)" />
    )}

    {/* Neck / shoulder line */}
    <path d="M20 55 Q32 48 44 55 L44 64 L20 64 Z" fill="#0B0B0B" />

    {/* Face */}
    <ellipse cx="32" cy="32" rx="14" ry="16" fill="url(#aria-skin)" />

    {/* Hair — soft framing bob */}
    <path
      d="M18 30 Q17 15 32 12 Q47 15 46 30 Q46 20 42 18 Q39 22 32 22 Q25 22 22 18 Q18 20 18 30 Z"
      fill="url(#aria-hair)"
    />
    {/* Hair sides (falls past cheeks) */}
    <path d="M17 30 Q17 40 21 44 L20 30 Z" fill="url(#aria-hair)" />
    <path d="M47 30 Q47 40 43 44 L44 30 Z" fill="url(#aria-hair)" />

    {/* Eyes — soft closed-happy shape (approachable, not staring) */}
    <path d="M25 33 Q27 31.5 29 33" stroke="#1F1B18" strokeWidth="1.4" strokeLinecap="round" fill="none" />
    <path d="M35 33 Q37 31.5 39 33" stroke="#1F1B18" strokeWidth="1.4" strokeLinecap="round" fill="none" />

    {/* Cheeks — subtle warmth */}
    <circle cx="25.5" cy="38" r="1.6" fill="#E89A88" opacity="0.55" />
    <circle cx="38.5" cy="38" r="1.6" fill="#E89A88" opacity="0.55" />

    {/* Friendly smile */}
    <path d="M28 41 Q32 44 36 41" stroke="#1F1B18" strokeWidth="1.4" strokeLinecap="round" fill="none" />

    {/* AI spark — top-right badge */}
    <g transform="translate(46 12)">
      <circle r="7" fill="#0B0B0B" />
      <path
        d="M0 -3.5 L1 -1 L3.5 0 L1 1 L0 3.5 L-1 1 L-3.5 0 L-1 -1 Z"
        fill="#C89B5D"
      />
    </g>
  </svg>
);

export default AIAssistantAvatar;
